import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

// Simple validation schema for optional endpoints
const endpointSchema = yup.object().shape({
  url: yup.string(),
  user: yup.string(),
  password: yup.string(),
  clientId: yup.string(),
  clientSecret: yup.string(),
  tlsCertificate: yup.string(),
});

// Main validation schema
const eiapClusterSchema = yup.object().shape({
  // Cluster Information
  name: yup
    .string()
    .required('Cluster name is required')
    .min(3, 'Cluster name must be at least 3 characters')
    .max(100, 'Cluster name must not exceed 100 characters'),
  category: yup
    .string()
    .required('Environment category is required')
    .oneOf(['Production', 'Staging', 'Development', 'Testing'], 'Invalid category'),
  description: yup
    .string()
    .notRequired()
    .max(500, 'Description must not exceed 500 characters'),

  // Endpoints
  rloEndpoint: endpointSchema,
  eicEndpoint: endpointSchema,
  eolmEndpoint: endpointSchema,
});

export type EiapClusterFormValues = yup.InferType<typeof eiapClusterSchema>;

export const useEiapClusterForm = (initialValues?: any) => {
  // Helper function to extract endpoint data from normalized structure
  const getEndpointData = (endpoints: any[], type: string) => {
    const endpoint = endpoints?.find(ep => ep.targetEnvToolType?.name === type);
    if (type === 'EIC') {
      return endpoint ? {
        url: endpoint.url || '',
        user: endpoint.user || '',
        password: endpoint.password || '',
        clientId: endpoint.clientId || '',
        clientSecret: endpoint.clientSecret || '',
        tlsCertificate: endpoint.tlsCertificate || '',
      } : {
        url: '',
        user: '',
        password: '',
        clientId: '',
        clientSecret: '',
        tlsCertificate: '',
      };
    }
    return endpoint ? {
      url: endpoint.url || '',
      user: endpoint.user || '',
      password: endpoint.password || '',
      clientId: '',
      clientSecret: '',
      tlsCertificate: endpoint.tlsCertificate || '',
    } : {
      url: '',
      user: '',
      password: '',
      clientId: '',
      clientSecret: '',
      tlsCertificate: '',
    };
  };

  const defaultValues: any = {
    name: initialValues?.name || '',
    category: initialValues?.category || 'Development',
    description: initialValues?.description || '',
    rloEndpoint: initialValues?.endpoints ? getEndpointData(initialValues.endpoints, 'RLO') : {
      url: '',
      user: '',
      password: '',
      clientId: '',
      clientSecret: '',
      tlsCertificate: '',
    },
    eicEndpoint: initialValues?.endpoints ? getEndpointData(initialValues.endpoints, 'EIC') : {
      url: '',
      user: '',
      password: '',
      clientId: '',
      clientSecret: '',
      tlsCertificate: '',
    },
    eolmEndpoint: initialValues?.endpoints ? getEndpointData(initialValues.endpoints, 'EOLM') : {
      url: '',
      user: '',
      password: '',
      clientId: '',
      clientSecret: '',
      tlsCertificate: '',
    },
  };

  return useForm({
    resolver: yupResolver(eiapClusterSchema),
    defaultValues,
    mode: 'onChange', // Changed from 'onBlur' to 'onChange' for better validation feedback
  });
};
